import { isFinalizedAttendance } from "../../../LambdaFunctions/shared/lessonAttendance";

export const ATTENDANCE_STATUS = {
  NOT_CHECKED: "notChecked",
  PARTIALLY_CANCELLED: "partiallyCancelled",
  CANCELLED: "cancelled",
  CHECKED: "checked",
};

const cancellationStatuses = ["cancelledOnTime", "cancelledLate"];

export const hasRecordedAttendanceDecision = student => (
  !!student &&
  typeof student.isPresent === "boolean" &&
  student.participationStatus !== "scheduled" &&
  !cancellationStatuses.includes(student.participationStatus)
);

const getCancelledStudentIds = students => new Set(
  students
    .filter(student => cancellationStatuses.includes(student.participationStatus))
    .map(student => student.id),
);

const getParticipantIds = (scheduledLesson = {}) => {
  const defaultStudents = scheduledLesson.defaultStudents || [];
  const registerStudents = (scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.students) || [];
  const ids = [
    ...defaultStudents.map(student => student.id),
    ...registerStudents.map(student => student.id),
  ].filter(Boolean);

  return [...new Set(ids)];
};

const hasRegisterForAllParticipants = (scheduledLesson = {}) => {
  const participantIds = getParticipantIds(scheduledLesson);
  if (participantIds.length === 0) return true;

  const registerStudents = (scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.students) || [];
  if (registerStudents.length === 0) return false;

  const registerStudentIds = new Set(registerStudents
    .filter(student => hasRecordedAttendanceDecision(student) || cancellationStatuses.includes(student.participationStatus))
    .map(student => student.id).filter(Boolean));

  return participantIds.every(studentId => registerStudentIds.has(studentId));
};

export function getLessonAttendanceStatus(scheduledLesson = {}) {
  const { lessonRegister } = scheduledLesson;
  if (!lessonRegister) return ATTENDANCE_STATUS.NOT_CHECKED;

  const details = lessonRegister.lessonDetails || {};
  if (details.lessonStatus === "cancelled") return ATTENDANCE_STATUS.CANCELLED;
  if (isFinalizedAttendance(lessonRegister)) return ATTENDANCE_STATUS.CHECKED;
  if (details.attendanceStatus === ATTENDANCE_STATUS.CHECKED && !hasRegisterForAllParticipants(scheduledLesson)) {
    return ATTENDANCE_STATUS.NOT_CHECKED;
  }
  if (details.attendanceStatus) return details.attendanceStatus;

  const students = lessonRegister.students || [];
  const hasCancellation = students.some(student => cancellationStatuses.includes(student.participationStatus));
  if (hasCancellation) return ATTENDANCE_STATUS.PARTIALLY_CANCELLED;

  return students.length > 0 && students.every(hasRecordedAttendanceDecision)
    ? ATTENDANCE_STATUS.CHECKED
    : ATTENDANCE_STATUS.NOT_CHECKED;
}

export function isAttendanceChecked(scheduledLesson) {
  return getLessonAttendanceStatus(scheduledLesson) === ATTENDANCE_STATUS.CHECKED;
}

export function isLessonCancelled(scheduledLesson) {
  return getLessonAttendanceStatus(scheduledLesson) === ATTENDANCE_STATUS.CANCELLED;
}

export function hasPartialCancellation(scheduledLesson) {
  return getLessonAttendanceStatus(scheduledLesson) === ATTENDANCE_STATUS.PARTIALLY_CANCELLED;
}

export function hasAllParticipantsCancelled(scheduledLesson = {}) {
  if (isLessonCancelled(scheduledLesson)) return true;

  const { lessonRegister } = scheduledLesson;
  if (!lessonRegister) return false;

  const participantIds = getParticipantIds(scheduledLesson);
  if (participantIds.length === 0) return false;

  const cancelledStudentIds = getCancelledStudentIds(lessonRegister.students || []);
  return participantIds.every(studentId => cancelledStudentIds.has(studentId));
}

export function isLessonPayoutEligible(scheduledLesson = {}) {
  const lesson = scheduledLesson.lessonRegister ? scheduledLesson : { lessonRegister: scheduledLesson, ...scheduledLesson };
  const register = lesson.lessonRegister;
  if (!register) return false;

  const details = register.lessonDetails || {};
  const students = register.students || [];

  // Późne odwołanie (cancelledLate) -> true
  const hasLateCancellation = students.some(s => s && s.participationStatus === "cancelledLate");
  if (hasLateCancellation) {
    return true;
  }

  // Wcześniejsze odwołanie (all participants cancelled on time) -> false
  if (students.length > 0 && students.every(s => s && s.participationStatus === "cancelledOnTime")) {
    return false;
  }

  // Odwołanie przez szkołę (CANCELLED) -> false
  if (details.lessonStatus === "cancelled" || details.attendanceStatus === ATTENDANCE_STATUS.CANCELLED) {
    return false;
  }

  // Lekcja CHECKED -> true
  const status = getLessonAttendanceStatus(lesson);
  if (status === ATTENDANCE_STATUS.CHECKED) {
    return true;
  }

  // Częściowe odwołanie: jeśli co najmniej jeden uczeń uczestniczył/miał decyzję -> true
  if (status === ATTENDANCE_STATUS.PARTIALLY_CANCELLED) {
    const hasActiveParticipant = students.some(s => hasRecordedAttendanceDecision(s));
    if (hasActiveParticipant) {
      return true;
    }
  }

  // Niesprawdzona (NOT_CHECKED) -> false
  return false;
}

export function isPayoutEligible(studentOrLesson, participationStatus, attendanceStatus) {
  if (studentOrLesson && typeof studentOrLesson === "object" && (studentOrLesson.lessonRegister || studentOrLesson.lessonDetails)) {
    return isLessonPayoutEligible(studentOrLesson);
  }

  let student = null;
  let partStatus = participationStatus;
  let attStatus = attendanceStatus;

  if (typeof studentOrLesson === "string") {
    partStatus = studentOrLesson;
    attStatus = participationStatus;
  } else if (studentOrLesson && typeof studentOrLesson === "object") {
    student = studentOrLesson;
    partStatus = participationStatus || student.participationStatus;
    attStatus = attendanceStatus || student.attendanceStatus;
  }

  // Późne odwołanie (cancelledLate) -> true
  if (partStatus === "cancelledLate") {
    return true;
  }

  // Wcześniejsze odwołanie (cancelledOnTime) -> false
  if (partStatus === "cancelledOnTime") {
    return false;
  }

  // Odwołanie przez szkołę (CANCELLED) -> false
  if (attStatus === ATTENDANCE_STATUS.CANCELLED || attStatus === "cancelled") {
    return false;
  }

  // Niesprawdzona (NOT_CHECKED) -> false
  if (attStatus === ATTENDANCE_STATUS.NOT_CHECKED || attStatus === "notChecked") {
    return false;
  }

  // Lekcja CHECKED -> true
  if (attStatus === ATTENDANCE_STATUS.CHECKED || attStatus === "checked") {
    return true;
  }

  if (student && typeof student.isPresent === "boolean") {
    return true;
  }

  return false;
}

